import { SyncCoreDataDefinitionsEnumTranslator, TaxonomyCategory, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faChevronDown, faTableColumns } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Form } from '@edgebox/react-components';
import { getCachedTaxonomies } from './useCachedTaxonomyData';
import {
  BP_POPOVER_CLASSES,
  BP_POPOVER_GROUP_LABEL_CLASSES,
  BP_TOOLBAR_BUTTON_CLASSES,
  BP_TOOLBAR_BUTTON_OPEN_CLASSES,
} from '../../../Components/brandPresenceClasses';
import { useDismissOnOutsideClick } from '../../../Components/useDismissOnOutsideClick';

interface IColumnEntry {
  facet: TaxonomyFacet;
  name: string;
  category?: TaxonomyCategory;
}

export type ISystemColumn = 'score' | 'status' | 'worstChecks' | 'priority' | 'locale';

interface ISystemColumnOption {
  id: ISystemColumn;
  label: string;
  group: 'health' | 'system';
}

const SYSTEM_COLUMN_OPTIONS: ISystemColumnOption[] = [
  { id: 'score', label: 'Score', group: 'health' },
  { id: 'worstChecks', label: 'Worst Checks', group: 'health' },
  { id: 'status', label: 'Status', group: 'system' },
  { id: 'priority', label: 'Priority', group: 'system' },
  { id: 'locale', label: 'Locale', group: 'system' },
];

export function ColumnPicker({
  api,
  selectedFacets,
  selectedSystemColumns,
  onToggleFacet,
  onToggleSystemColumn,
}: {
  api?: ISyncCoreApi;
  selectedFacets: TaxonomyFacet[];
  selectedSystemColumns: ISystemColumn[];
  onToggleFacet: (facet: TaxonomyFacet) => void;
  onToggleSystemColumn: (column: ISystemColumn) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const closePicker = React.useCallback(() => setOpen(false), []);
  useDismissOnOutsideClick({ open, containerRef, triggerRef, onDismiss: closePicker });
  const [entries, setEntries] = React.useState<IColumnEntry[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || !api) {
      return;
    }

    let active = true;
    void (async () => {
      setLoading(true);
      const taxonomies = await getCachedTaxonomies(api);
      if (!active) {
        return;
      }

      setEntries(
        taxonomies
          .filter((taxonomy) => !!taxonomy.facet)
          .map((taxonomy) => ({
            facet: taxonomy.facet as TaxonomyFacet,
            name: taxonomy.name,
            category: taxonomy.category as TaxonomyCategory,
          }))
      );
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [api, open]);

  const groupedTaxonomyEntries = React.useMemo(() => {
    const byCategory = new Map<string, { id: string; label: string; entries: IColumnEntry[] }>();

    for (const entry of entries) {
      const category = entry.category;
      if (!category) {
        continue;
      }

      const existing = byCategory.get(category);
      if (!existing) {
        byCategory.set(category, {
          id: category,
          label: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyCategory', category),
          entries: [entry],
        });
        continue;
      }

      existing.entries.push(entry);
    }

    return Array.from(byCategory.values())
      .map((group) => ({
        ...group,
        entries: [...group.entries].sort((left, right) => left.name.localeCompare(right.name)),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [entries]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={open ? BP_TOOLBAR_BUTTON_OPEN_CLASSES : BP_TOOLBAR_BUTTON_CLASSES}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={faTableColumns} className="text-xs" /> Edit columns
      </button>

      {open ? (
        <div className={`absolute right-0 z-20 mt-2 w-72 ${BP_POPOVER_CLASSES}`}>
          <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
            <div className={`mb-2 ${BP_POPOVER_GROUP_LABEL_CLASSES}`}>System</div>
            <div className="space-y-1 mb-3">
              {SYSTEM_COLUMN_OPTIONS.filter((option) => option.group === 'system').map((option) => (
                <label
                  key={option.id}
                  className="flex items-center gap-2 rounded-lg px-1.5 py-1 cursor-pointer transition-colors hover:bg-slate-50"
                >
                  <Form.Check
                    type="checkbox"
                    className="mb-0"
                    checked={selectedSystemColumns.includes(option.id)}
                    onChange={() => onToggleSystemColumn(option.id)}
                  />
                  <span className="text-sm text-slate-700">{option.label}</span>
                </label>
              ))}
            </div>

            <div className={`mb-2 ${BP_POPOVER_GROUP_LABEL_CLASSES}`}>Health</div>
            <div className="space-y-1 mb-3">
              {SYSTEM_COLUMN_OPTIONS.filter((option) => option.group === 'health').map((option) => (
                <label
                  key={option.id}
                  className="flex items-center gap-2 rounded-lg px-1.5 py-1 cursor-pointer transition-colors hover:bg-slate-50"
                >
                  <Form.Check
                    type="checkbox"
                    className="mb-0"
                    checked={selectedSystemColumns.includes(option.id)}
                    onChange={() => onToggleSystemColumn(option.id)}
                  />
                  <span className="text-sm text-slate-700">{option.label}</span>
                </label>
              ))}
            </div>

            {loading ? <div className="text-sm text-slate-500">Loading taxonomies…</div> : null}
            {groupedTaxonomyEntries.map((group) => (
              <div key={group.id}>
                <div className={`px-1 py-1 ${BP_POPOVER_GROUP_LABEL_CLASSES}`}>{group.label}</div>
                <div className="space-y-1">
                  {group.entries.map((entry) => (
                    <label
                      key={entry.facet}
                      className="flex items-center gap-2 rounded-lg px-1.5 py-1 cursor-pointer transition-colors hover:bg-slate-50"
                    >
                      <Form.Check
                        type="checkbox"
                        className="mb-0"
                        checked={selectedFacets.includes(entry.facet)}
                        onChange={() => onToggleFacet(entry.facet)}
                      />
                      <span className="text-sm text-slate-700">{entry.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="mt-3 inline-flex items-center text-xs text-slate-500 hover:text-slate-700"
            onClick={() => setOpen(false)}
          >
            Close <FontAwesomeIcon icon={faChevronDown} className="text-[10px] ml-1 rotate-180" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
